import { useContext, useMemo } from 'react'
import moment from 'moment'
import usePublishSalesCopilotStay from 'components/OfferPage/SalesCopilot/usePublishSalesCopilotStay'
import { getDefaultDepositAmountPercentage } from 'checkout/selectors/featureConfig/deposit'
import { useOfferPageRate } from 'components/OfferPage/hooks/useOfferPageRate'
import useSurchargeMargin from 'hooks/Offers/useSurchargeMarginFetcher'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import CurrencyContext from 'contexts/currencyContext'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import { useAppSelector } from 'hooks/reduxHooks'
import { isPackageEligibleForZeroReservation } from 'lib/offer/reserveForZeroUtils'
import { getDepositAmountPercentage, getPaymentDueDate, isDepositAvailableOnOffer } from 'lib/payment/depositsUtils'
import { getCheckoutDate, getIsFlightsSelected } from 'selectors/offerPage/offerPageSelectors'

interface Props {
  offer: App.HotelOffer
}

// Publishes the agent's chosen stay so the app-level Sales Co-Pilot sidebar can read it.
function SalesCopilotOfferBridge({ offer }: Props) {
  const offerPageState = useContext(OfferPageStateContext)
  const packageView = useOfferPageRate(offerPageState)
  const defaultDepositPercentage = useAppSelector(getDefaultDepositAmountPercentage)
  const currentCurrency = useContext(CurrencyContext)

  const checkIn = offerPageState.checkInDate
  const checkOut = getCheckoutDate(offerPageState)
  const flightsSelected = getIsFlightsSelected(offerPageState)
  const canViewLuxPlusBenefits = useAppSelector(getCanViewLuxPlusBenefits)
  const pkg = packageView?.package
  const { surchargeMargin } = useSurchargeMargin({ checkIn, duration: pkg?.duration ?? 0, roomRateId: pkg?.roomRate?.id })
  const surchargeMarginAud = surchargeMargin?.status === 'success' ? surchargeMargin.marginAud : 0

  const offerStay = useMemo<App.SalesCopilotOfferStay | undefined>(() => {
    if (!checkIn || !checkOut || !packageView) return undefined

    const checkInMoment = moment(checkIn)
    const cancellationPolicyType = packageView.package.roomRate?.cancellationPolicy?.type
    const total = packageView.hotelTotal + (flightsSelected ? packageView.flightPrice : 0)
    // Mirrors FlashHotelMarginText: the member discount comes straight out of margin.
    const memberDiscount = canViewLuxPlusBenefits ? packageView.package.price - packageView.package.memberPrice : 0
    const marginAud = packageView.package.marginAud
    const hotelMargin = Number.isFinite(marginAud) ? (marginAud ?? 0) - memberDiscount + surchargeMarginAud : undefined

    return {
      offerId: offer.id,
      offerType: offer.type,
      offerName: offer.property?.name ?? offer.name,
      checkIn,
      checkOut,
      nights: packageView.package.duration,
      currencyCode: currentCurrency,
      total,
      hotelTotal: packageView.hotelTotal,
      hotelMargin,
      flightsSelected,
      depositAvailable: isDepositAvailableOnOffer(offer, currentCurrency, checkInMoment, packageView.hotelTotal, packageView.package),
      depositPercentage: getDepositAmountPercentage(offer, defaultDepositPercentage),
      depositDueDays: getPaymentDueDate(offer.depositThresholds, offer.type, cancellationPolicyType),
      reserveForZeroAvailable: isPackageEligibleForZeroReservation(offer, checkInMoment, cancellationPolicyType),
      availableRooms: packageView.availableRooms,
      offerEndsAt: offer.onlinePurchaseSchedule?.end,
      bookedLast72Hours: offer.stats?.booked,
      valuedAt: packageView.pricing.value,
      discountPercent: packageView.pricing.discountPercent,
    }
  }, [checkIn, checkOut, packageView, flightsSelected, offer, currentCurrency, defaultDepositPercentage, canViewLuxPlusBenefits, surchargeMarginAud])

  usePublishSalesCopilotStay(offerStay)

  return null
}

export default SalesCopilotOfferBridge
